import { type LinearScale, niceScale, valueDomain } from './chart.lib';
import type { TimeSeries, TimeSeriesBreak, TimeSeriesChartSeries } from './model/time-series.types';

/** 그리는 데 쓰는 시계열 자료(그리는 방법과 상관없는 셈 결과) */
export interface TimeSeriesModel {
  /** 가로축 기간 이름. 가장 긴 계열의 순서 */
  labels: string[];
  /** 계열마다 기간 순서의 값. null = 빈 기간(0 으로 바꾸지 않는다) */
  values: (number | null)[][];
  /** 왼쪽 축. 그릴 값이 없으면 null */
  left: LinearScale | null;
  /** 오른쪽 축(series=dual-axis 의 둘째 계열) */
  right: LinearScale | null;
  /** 계열마다 쓰는 축 */
  axisOf: ('left' | 'right')[];
  /** 앞 기간과 잇지 않는 기간 번호(끊김) */
  breakBefore: Set<number>;
}

/** 계열은 두 개까지(series 축이 double · dual-axis 까지) */
const MAX_SERIES = 2;

export function timeSeriesModel(
  data: readonly TimeSeries[],
  series: TimeSeriesChartSeries,
  breaks: readonly TimeSeriesBreak[] = [],
): TimeSeriesModel {
  const used = data.slice(0, series === 'single' ? 1 : MAX_SERIES);
  const longest = used.reduce<TimeSeries | undefined>(
    (acc, item) => (acc === undefined || item.points.length > acc.points.length ? item : acc),
    undefined,
  );
  const labels = longest?.points.map((point) => point.label) ?? [];
  const values = used.map((item) =>
    labels.map((_, index) => {
      const value = item.points[index]?.value;
      return value === undefined || value === null || !Number.isFinite(value) ? null : value;
    }),
  );
  const axisOf = used.map((_, index) =>
    series === 'dual-axis' && index === 1 ? ('right' as const) : ('left' as const),
  );
  const scaleOf = (side: 'left' | 'right') => {
    const own = values.filter((_, index) => axisOf[index] === side).flat();
    if (!own.some((value) => value !== null)) return side === 'left' ? niceScale(0, 10) : null;
    const domain = valueDomain(own);
    return niceScale(domain.min, domain.max);
  };
  const hasValue = values.some((row) => row.some((value) => value !== null));
  return {
    labels,
    values,
    left: hasValue ? scaleOf('left') : null,
    right: hasValue && axisOf.includes('right') ? scaleOf('right') : null,
    axisOf,
    breakBefore: new Set(
      breaks.map((item) => item.beforeIndex).filter((index) => index > 0 && index < labels.length),
    ),
  };
}

/**
 * 선을 이을 구간: 빈 값(null)과 끊김(breakBefore)에서 나눈다. 각 구간은 기간 번호 목록.
 * 점 하나짜리 구간은 선 · 면 없이 점만 그린다.
 */
export function lineSegments(
  values: readonly (number | null)[],
  breakBefore: Set<number>,
): number[][] {
  const segments: number[][] = [];
  let current: number[] = [];
  values.forEach((value, index) => {
    if (value === null || breakBefore.has(index)) {
      if (current.length > 0) segments.push(current);
      current = [];
    }
    if (value !== null) current.push(index);
  });
  if (current.length > 0) segments.push(current);
  return segments;
}

/** 막대 그리는 순서: 한 기간에서 큰 값(절댓값)부터 — 같은 자리에 겹치는 작은 막대가 앞에 오게 한다 */
export function barDrawOrder(values: readonly (number | null)[][], index: number): number[] {
  return values
    .map((row, seriesIndex) => ({ seriesIndex, value: row[index] }))
    .filter(
      (item): item is { seriesIndex: number; value: number } =>
        item.value !== null && item.value !== undefined,
    )
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .map((item) => item.seriesIndex);
}

/** 가로축 글자를 몇 개마다 하나씩 적을지(좁으면 건너뛴다) */
export function labelStride(columnWidth: number, labelWidth: number): number {
  if (columnWidth <= 0) return 1;
  return Math.max(1, Math.ceil(labelWidth / columnWidth));
}
